/* Krelune commerce: native Shopify records, locale-aware Cart API, no external runtime. */
(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('KrCommerceConfig')?.textContent || '{}');
  const root = (window.Shopify?.routes?.root || config.root || '/').replace(/\/?$/, '/');
  const money = (cents, currency = config.currency || 'EUR') => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents) / 100);
  const define = (name, component) => { if (!customElements.get(name)) customElements.define(name, component); };
  let refreshTimer, refreshInFlight = false, refreshAgain = false, lastCart;
  async function refreshCart() {
    if (refreshInFlight) { refreshAgain = true; return; }
    refreshInFlight = true;
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      lastCart = await response.json();
      document.querySelectorAll('kr-cart-goals').forEach(el => el.update(lastCart));
      document.querySelectorAll('kr-cart-upsells').forEach(el => el.update(lastCart));
    } catch {
      document.querySelectorAll('kr-cart-goals').forEach(el => el.hidden = true);
    } finally {
      refreshInFlight = false;
      if (refreshAgain) { refreshAgain = false; queueRefresh(); }
    }
  }
  function queueRefresh() { clearTimeout(refreshTimer); refreshTimer = setTimeout(refreshCart, 80); }
  async function add(form, trigger) {
    const id = Number(new FormData(form).get('id'));
    const quantity = Number(new FormData(form).get('quantity') || 1);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error(config.error);
    const formData = new FormData(form);
    const item = { id, quantity };
    const sellingPlan = Number(formData.get('selling_plan'));
    if (Number.isSafeInteger(sellingPlan) && sellingPlan > 0) item.selling_plan = sellingPlan;
    for (const [name, value] of formData) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') (item.properties ||= {})[match[1]] = value;
    }
    const drawer = document.querySelector('cart-drawer');
    const sectionIds = drawer?.getSectionsToRender().map(s => s.id) || ['cart-icon-bubble'];
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sectionIds, sections_url: location.pathname })
    });
    const cartData = await response.json();
    if (!response.ok || cartData.status) throw new Error(cartData.description || config.error);
    const dialog = trigger.closest('dialog');
    if (dialog?.open) dialog.close();
    queueRefresh();
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kr-quick-add', productVariantId: id, cartData });
    // A successful add with unavailable section HTML must never be retried as an add.
    const ready = drawer && sectionIds.every(id => typeof cartData.sections?.[id] === 'string' && cartData.sections[id].trim());
    if (ready) {
      drawer.setActiveElement(trigger.isConnected ? trigger : document.querySelector('#cart-icon-bubble'));
      drawer.classList.remove('is-empty');
      try { drawer.renderContents(cartData); } catch { location.assign(`${root}cart`); }
    } else location.assign(`${root}cart`);
    return cartData;
  }
  window.KreluneCommerce = { money, refresh: queueRefresh, add };
  if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, queueRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) queueRefresh(); });
  window.addEventListener('pageshow', event => { if (event.persisted) queueRefresh(); });

  define('kr-cart-goals', class extends HTMLElement {
    connectedCallback() {
      this.update(lastCart || { total_price: Number(this.dataset.total), currency: this.dataset.currency });
      queueRefresh();
    }
    update(cart) {
      if (cart.currency !== this.dataset.currency || !Number.isFinite(cart.total_price)) { this.hidden = true; return; }
      this.hidden = false;
      this.querySelectorAll('[data-goal]').forEach(goal => {
        const threshold = Number(goal.dataset.threshold);
        if (threshold <= 0) { goal.hidden = true; return; }
        const remainder = Math.max(0, threshold - cart.total_price);
        const percentage = Math.max(0, Math.min(100, cart.total_price / threshold * 100));
        goal.querySelector('[data-goal-message]').textContent = remainder ? goal.dataset.pending.replaceAll('[amount]', money(remainder, cart.currency)) : goal.dataset.complete;
        const bar = goal.querySelector('[role=progressbar]');
        bar.setAttribute('aria-valuenow', String(Math.round(percentage)));
        bar.setAttribute('aria-valuetext', goal.querySelector('p').textContent);
        bar.querySelector('span').style.width = `${percentage}%`;
      });
    }
  });
  define('kr-cart-upsells', class extends HTMLElement {
    connectedCallback() { this.update(lastCart); }
    update(cart) {
      const ids = new Set(cart?.items?.map(item => String(item.product_id)) || []);
      this.querySelectorAll('[data-product-id]').forEach(el => { el.hidden = ids.has(el.dataset.productId); });
      this.hidden = ![...this.querySelectorAll('[data-product-id]')].some(el => !el.hidden);
    }
  });
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.form?.querySelector('[type=submit]');
      if (!this.form || !this.button) return;
      this.form.addEventListener('submit', event => this.submit(event), { signal: this.abort.signal });
      this.form.addEventListener('change', () => this.sync(), { signal: this.abort.signal });
      this.sync();
    }
    sync() {
      const select = this.querySelector('select[name=id]');
      const option = select?.selectedOptions[0];
      if (option) {
        const price = this.querySelector('[data-quick-price]');
        if (price) price.textContent = option.dataset.price;
        this.form.querySelector('[name=quantity]').value = option.dataset.min || '1';
        this.button.disabled = option.disabled;
      }
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || this.button.disabled) return;
      this.busy = true;
      const status = this.querySelector('.kr-form-status');
      const label = this.button.textContent;
      const fields = [...this.form.querySelectorAll('button,select,input')].map(el => [el, el.disabled]);
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = config.working;
      if (status) { status.hidden = true; status.textContent = ''; }
      // Snapshot the form before disabling controls; disabled inputs are omitted by FormData.
      const snapshot = document.createElement('form');
      for (const key of ['id','quantity']) { const input = document.createElement('input'); input.name = key; input.value = new FormData(this.form).get(key) || '1'; snapshot.append(input); }
      fields.forEach(([el]) => el.disabled = true);
      try { await add(snapshot, this.button); if (status) { status.textContent = config.added; status.hidden = false; } }
      catch (error) { if (status) { status.textContent = error.message || config.error; status.hidden = false; } }
      finally {
        fields.forEach(([el, disabled]) => el.disabled = disabled);
        this.button.textContent = label;
        this.button.removeAttribute('aria-busy');
        this.busy = false;
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  define('kr-quick-add', QuickAdd);
  define('kr-bundle-builder', class extends QuickAdd {
    sync() {
      const enabled = [...this.querySelectorAll('input[type=radio]')].filter(el => !el.disabled);
      const selected = enabled.find(el => el.checked) || enabled[0];
      this.button.disabled = !selected;
      if (!selected) return;
      selected.checked = true;
      this.form.querySelector('input[name=id]').value = selected.value;
      this.form.querySelector('input[name=quantity]').value = selected.dataset.quantity;
      this.querySelector('[data-bundle-total]').textContent = selected.dataset.total;
    }
  });

  define('kr-story-nav', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mode !== 'filter') return;
      this.abort = new AbortController();
      this.tabs = [...this.querySelectorAll('[role=tab]')];
      this.addEventListener('click', event => { const tab = event.target.closest('[role=tab]'); if (tab) { event.preventDefault(); this.select(tab); } }, { signal: this.abort.signal });
      this.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0 || !['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next]); this.tabs[next].focus();
      }, { signal: this.abort.signal });
    }
    select(tab) {
      this.tabs.forEach(el => { const active = el === tab; el.setAttribute('aria-selected', String(active)); el.tabIndex = active ? 0 : -1; document.getElementById(el.getAttribute('aria-controls')).hidden = !active; });
    }
    disconnectedCallback() { this.abort?.abort(); }
  });
  define('kr-lookbook', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const options = { signal: this.abort.signal };
      this.querySelectorAll('details').forEach(detail => detail.addEventListener('toggle', () => { if (detail.open) this.closeOthers(detail); }, options));
      document.addEventListener('click', event => { if (!this.contains(event.target)) this.closeOthers(); }, options);
      this.addEventListener('keydown', event => { if (event.key === 'Escape') { const open = this.querySelector('details[open]'); this.closeOthers(); open?.querySelector('summary').focus(); } }, options);
    }
    closeOthers(active) { this.querySelectorAll('details').forEach(detail => { if (detail !== active) detail.open = false; }); }
    disconnectedCallback() { this.abort?.abort(); }
  });
  define('kr-feature-explorer', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        const pin = event.target.closest('[data-feature]');
        if (pin) { const detail = this.querySelector(`[data-feature-detail="${CSS.escape(pin.dataset.feature)}"]`); detail.open = true; this.sync(detail); }
      }, options);
      this.querySelectorAll('details').forEach(detail => detail.addEventListener('toggle', () => this.sync(detail.open ? detail : null), options));
    }
    sync(active) {
      if (active) this.querySelectorAll('details').forEach(detail => { if (detail !== active) detail.open = false; });
      this.querySelectorAll('[data-feature]').forEach(pin => pin.setAttribute('aria-expanded', String(this.querySelector(`[data-feature-detail="${CSS.escape(pin.dataset.feature)}"]`)?.open || false)));
    }
    disconnectedCallback() { this.abort?.abort(); }
  });
  define('kr-before-after', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.onInput = () => { this.style.setProperty('--kr-split', `${this.input.value}%`); this.input.setAttribute('aria-valuetext', `${this.input.value}%`); };
      this.input.addEventListener('input', this.onInput); this.onInput();
    }
    disconnectedCallback() { this.input?.removeEventListener('input', this.onInput); }
  });
  define('kr-comparison', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-compare-filter]'); if (!button) return;
        this.querySelectorAll('[data-compare-filter]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
        this.querySelectorAll('[data-comparison-row]').forEach(row => {
          const values = [...row.querySelectorAll('td')].map(cell => cell.textContent.trim().replace(/\s+/g,' ').toLowerCase());
          row.hidden = button.dataset.compareFilter === 'differences' && values.length > 1 && new Set(values).size === 1;
        });
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  });
  define('kr-press-marquee', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const opts = { signal: this.abort.signal };
      this.quotes = [...this.querySelectorAll('[data-quote]')];
      this.logos = [...this.querySelectorAll('.kr-marquee-group:not([inert]) [data-quote-index]')];
      this.index = 0;
      this.paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.pauseButton = this.querySelector('[data-marquee-pause]');
      this.addEventListener('click', event => {
        if (event.target.closest('[data-marquee-pause]')) { this.paused = !this.paused; this.motion(); }
        const logo = event.target.closest('[data-quote-index]');
        if (logo) { this.show(logo.dataset.quoteIndex); this.paused = true; this.motion(); }
      }, opts);
      this.motion(); if (this.logos[0]) this.show(this.logos[0].dataset.quoteIndex);
      this.timer = setInterval(() => {
        if (this.paused || document.hidden || this.matches(':hover') || this.contains(document.activeElement) || !this.logos.length) return;
        this.index = (this.index + 1) % this.logos.length; this.show(this.logos[this.index].dataset.quoteIndex);
      }, 5500);
    }
    show(index) { this.quotes.forEach(q => q.hidden = q.dataset.quote !== String(index)); }
    motion() { this.classList.toggle('kr-paused', this.paused); this.pauseButton?.setAttribute('aria-pressed', String(this.paused)); if (this.pauseButton) this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.play : this.pauseButton.dataset.pause; }
    disconnectedCallback() { clearInterval(this.timer); this.abort?.abort(); }
  });
  define('kr-shoppable-videos', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const opts = { signal: this.abort.signal };
      this.previews = [...this.querySelectorAll('.kr-video-preview')];
      this.paused = this.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        const video = entry.target; video.dataset.inView = String(entry.isIntersecting);
        if (entry.isIntersecting && !this.paused && !document.hidden && !this.querySelector('dialog[open]')) video.play().catch(() => {}); else video.pause();
      }), { threshold: 0.5 });
      this.previews.forEach(video => { this.observer.observe(video); video.addEventListener('play', () => video.parentElement.classList.add('kr-is-playing'), opts); video.addEventListener('pause', () => video.parentElement.classList.remove('kr-is-playing'), opts); });
      this.addEventListener('click', event => {
        const opener = event.target.closest('[data-open-video]');
        if (opener) {
          this.previews.forEach(v => v.pause());
          this.querySelectorAll('dialog[open]').forEach(d => d.close());
          const dialog = document.getElementById(opener.dataset.openVideo);
          this.opener = opener; dialog.showModal(); document.body.classList.add('kr-modal-open');
          dialog.querySelector('video')?.play().catch(() => {});
        }
        if (event.target.closest('[data-close-video]')) event.target.closest('dialog').close();
        if (event.target.matches('dialog')) { const r = event.target.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) event.target.close(); }
        const pause = event.target.closest('[data-pause-videos]');
        if (pause) { this.paused = !this.paused; pause.setAttribute('aria-pressed', String(this.paused)); this.updatePreviews(); }
      }, opts);
      this.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('close', () => { dialog.querySelector('video')?.pause(); document.body.classList.remove('kr-modal-open'); this.opener?.focus(); this.updatePreviews(); }, opts));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.querySelectorAll('video').forEach(v => v.pause()); else this.updatePreviews(); }, opts);
      document.addEventListener('shopify:section:unload', () => this.querySelectorAll('dialog[open]').forEach(d => d.close()), opts);
    }
    updatePreviews() { this.previews.forEach(v => { if (!this.paused && v.dataset.inView === 'true' && !document.hidden && !this.querySelector('dialog[open]')) v.play().catch(() => {}); else v.pause(); }); }
    disconnectedCallback() { this.querySelectorAll('video').forEach(v => v.pause()); if (this.querySelector('dialog[open]')) document.body.classList.remove('kr-modal-open'); this.observer?.disconnect(); this.abort?.abort(); }
  });
})();
